import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, User, FileText, X, Loader2 } from "lucide-react";
import { Input } from "./ui/input";

export default function GlobalSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ clients: [], policies: [], total: 0 });
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Debounce search
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const performSearch = useCallback(
    debounce(async (searchQuery) => {
      if (searchQuery.length < 2) {
        setResults({ clients: [], policies: [], total: 0 });
        return;
      }

      setLoading(true);
      try {
        const response = await axios.get(`/api/search?q=${encodeURIComponent(searchQuery)}&limit=8`);
        setResults(response.data);
        setSelectedIndex(-1);
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    if (query) {
      performSearch(query);
      setIsOpen(true);
    } else {
      setResults({ clients: [], policies: [], total: 0 });
      setIsOpen(false);
    }
  }, [query, performSearch]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    const allResults = [...results.clients, ...results.policies];
    
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, allResults.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === "Enter" && selectedIndex >= 0) {
      e.preventDefault();
      const selected = allResults[selectedIndex];
      handleSelect(selected);
    } else if (e.key === "Escape") {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const handleSelect = (item) => {
    setQuery("");
    setIsOpen(false);
    if (item.type === "client") {
      navigate(`/clients/${item.id}`);
    } else if (item.type === "policy") {
      navigate(`/policies/${item.id}`);
    }
  };

  // Keyboard shortcut (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleShortcut = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleShortcut);
    return () => document.removeEventListener("keydown", handleShortcut);
  }, []);

  const allResults = [...results.clients, ...results.policies];

  return (
    <div ref={searchRef} className="relative w-full max-w-md" data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Cerca clienti, polizze, targhe... (Ctrl+K)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className="pl-10 pr-10 bg-background/50 border-border/50 focus:bg-background"
          data-testid="global-search-input"
        />
        {query && (
          <button
            onClick={() => {
              setQuery("");
              setIsOpen(false);
            }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {loading && (
          <Loader2 className="absolute right-10 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {/* Results dropdown */}
      {isOpen && (query.length >= 2) && (
        <div 
          className="absolute top-full left-0 right-0 mt-2 bg-popover border border-border rounded-lg shadow-lg z-50 max-h-96 overflow-auto"
          data-testid="search-results"
        >
          {results.total === 0 && !loading ? (
            <div className="p-4 text-center text-muted-foreground">
              Nessun risultato per "{query}"
            </div>
          ) : (
            <>
              {/* Clients */}
              {results.clients.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-muted-foreground bg-muted/50 border-b border-border">
                    CLIENTI ({results.clients.length})
                  </div>
                  {results.clients.map((client, idx) => (
                    <button
                      key={client.id}
                      onClick={() => handleSelect(client)}
                      className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-accent text-left transition-colors ${
                        selectedIndex === idx ? "bg-accent" : ""
                      }`}
                      data-testid={`search-result-client-${idx}`}
                    >
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
                        <User className="h-4 w-4 text-blue-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{client.title}</div>
                        {client.subtitle && (
                          <div className="text-xs text-muted-foreground truncate">{client.subtitle}</div>
                        )}
                      </div>
                      {client.extra && (
                        <div className="text-xs text-muted-foreground">{client.extra}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Policies */}
              {results.policies.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-muted-foreground bg-muted/50 border-b border-border">
                    POLIZZE ({results.policies.length})
                  </div>
                  {results.policies.map((policy, idx) => (
                    <button
                      key={policy.id}
                      onClick={() => handleSelect(policy)}
                      className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-accent text-left transition-colors ${
                        selectedIndex === results.clients.length + idx ? "bg-accent" : ""
                      }`}
                      data-testid={`search-result-policy-${idx}`}
                    >
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                        <FileText className="h-4 w-4 text-green-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{policy.title}</div>
                        {policy.subtitle && (
                          <div className="text-xs text-muted-foreground truncate">{policy.subtitle}</div>
                        )}
                      </div>
                      {policy.extra && (
                        <div className="text-xs text-muted-foreground whitespace-nowrap">{policy.extra}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Footer */}
              {results.total > 0 && (
                <div className="px-3 py-2 text-xs text-center text-muted-foreground bg-muted/30 border-t border-border">
                  Premi <kbd className="px-1 py-0.5 bg-muted rounded text-xs">↵</kbd> per selezionare, <kbd className="px-1 py-0.5 bg-muted rounded text-xs">↑↓</kbd> per navigare
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
